import { SKILL_LIST } from "@mathtrain/engine";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar } from "../components/Avatar";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { timeAgo, useLoad } from "../lib/hooks";
import type { Challenge, PublicUser } from "../lib/types";

function Row({ c, onPlay }: { c: Challenge; onPlay?(): void }) {
  const t = c.theirs as any;
  const line = c.status === "done"
    ? c.outcome === "won" ? `You won, ${c.mine?.score} to ${t?.score}` : c.outcome === "lost" ? `They won, ${t?.score} to ${c.mine?.score}` : `Tie at ${c.mine?.score}`
    : c.status === "expired" ? "Expired"
    : c.mine ? `You scored ${c.mine.score}. Waiting for ${c.opponent.displayName}${t?.played ? "" : ""}.`
    : t?.played ? `${c.opponent.displayName} has played. Your turn.` : `${c.incoming ? "From" : "To"} ${c.opponent.displayName}`;
  return <li>
    <Avatar user={c.opponent} />
    <div className="who"><b>{c.opponent.displayName}</b>
      <span>{line}. {c.length} problems{c.skills.length < SKILL_LIST.length ? `, ${c.skills.length} skill${c.skills.length > 1 ? "s" : ""}` : ""}, {timeAgo(c.createdAt)}</span>
      {c.message && <span style={{ display: "block" }}>“{c.message}”</span>}
    </div>
    {onPlay && <button className="btn ink" style={{ padding: "8px 14px" }} onClick={onPlay}>Play</button>}
    {c.status === "done" && <span className={c.outcome === "won" ? "up" : c.outcome === "lost" ? "dn" : "mute"}>{c.outcome === "won" ? "W" : c.outcome === "lost" ? "L" : "T"}</span>}
  </li>;
}

export function Duels() {
  const { user } = useAuth();
  const nav = useNavigate();
  const list = useLoad(() => api<{ toPlay: Challenge[]; waiting: Challenge[]; finished: Challenge[] }>("/challenges"));
  const users = useLoad(() => api<{ users: PublicUser[] }>("/users"));
  const [opp, setOpp] = useState<string>((useLocation().state as any)?.opponent ?? "");
  const [skills, setSkills] = useState<Set<string>>(new Set());
  const [len, setLen] = useState(10);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const others = users.data?.users.filter((u) => u.username !== user!.username) ?? [];

  const send = async () => {
    setErr("");
    try {
      const r = await api<{ challenge: Challenge }>("/challenges", { body: { opponent: opp, skills: [...skills], length: len, message: msg } });
      nav("/play", { state: { mode: "challenge", challengeId: r.challenge.id } });
    } catch (e: any) { setErr(e.message); }
  };
  const toggle = (k: string) => { const s = new Set(skills); s.has(k) ? s.delete(k) : s.add(k); setSkills(s); };
  const d = list.data;

  return <main className="page">
    <h1>Duels</h1>
    <p className="mute">Challenge a friend to the same set of problems. Difficulty sits between your levels, so it's fair both ways. Best score wins; ties go to the faster player.</p>

    {d && d.toPlay.length > 0 && <><h2>Your move</h2><div className="card"><ul className="board">
      {d.toPlay.map((c) => <Row key={c.id} c={c} onPlay={() => nav("/play", { state: { mode: "challenge", challengeId: c.id } })} />)}</ul></div></>}

    <h2>New duel</h2>
    <div className="card">
      {others.length === 0 ? <div className="empty">Invite someone from the Me tab to start dueling.</div> : <>
        <div className="small mute" style={{ marginBottom: 6 }}>Opponent</div>
        <div className="chips">{others.map((u) => <button key={u.username} aria-pressed={opp === u.username} onClick={() => setOpp(u.username)}>{u.displayName}</button>)}</div>
        <div className="small mute" style={{ margin: "12px 0 6px" }}>Skills (none picked means all)</div>
        <div className="chips">{SKILL_LIST.map((s) => <button key={s.key} aria-pressed={skills.has(s.key)} onClick={() => toggle(s.key)}>{s.name}</button>)}</div>
        <div className="seg" style={{ margin: "12px 0" }}>{[5, 10, 20].map((n) => <button key={n} aria-pressed={len === n} onClick={() => setLen(n)}>{n} problems</button>)}</div>
        <label className="field"><span>Message (optional)</span><input value={msg} maxLength={140} onChange={(e) => setMsg(e.target.value)} placeholder="Loser makes dinner" /></label>
        <div className="error">{err}</div>
        <button className="btn-primary" disabled={!opp} onClick={send}>{opp ? "Send and play your round" : "Pick an opponent"}</button>
      </>}
    </div>

    {d && d.waiting.length > 0 && <><h2>Waiting on them</h2><div className="card"><ul className="board">{d.waiting.map((c) => <Row key={c.id} c={c} />)}</ul></div></>}
    {d && d.finished.length > 0 && <><h2>Finished</h2><div className="card"><ul className="board">{d.finished.map((c) => <Row key={c.id} c={c} />)}</ul></div></>}
  </main>;
}
